import { confirmKrPaymentOrder, getKrPaymentOrder } from '@entities/kr-payment/api'
import {
  isKrOrderRetired,
  krOrderResumePath,
  type KrPaymentOrder,
  type PrepareKrPayment,
} from '@entities/kr-payment/model'
import type { PaymentCustomData } from '@features/payment/model/portone.model'
import { parseKrPaymentReturnTarget } from '@shared/config/auth/kr-payment-return'
import { navigateToPaymentAwareAuthRecovery, withKrPaymentAuthReturn } from '@shared/libs/kr-payment-auth-navigation'
import { z } from 'zod'

export function preparedCustomData(customData: PaymentCustomData, order?: KrPaymentOrder | null): PaymentCustomData {
  return order ? { ...customData, product_id: order.productId, purchase_order_id: order.orderId } : customData
}

export function preparedPaymentUrl(url: URL, order?: KrPaymentOrder | null): URL {
  if (!order) {
    return url
  }
  const preparedUrl = new URL(url)
  preparedUrl.searchParams.set('purchase_order_id', order.orderId)
  preparedUrl.searchParams.set('product_id', order.productId)
  return preparedUrl
}

export async function requestPreparedProvider<T>(
  request: () => Promise<T>,
  preparation?: PrepareKrPayment,
  orderId?: string,
): Promise<T> {
  try {
    return await request()
  } catch (error) {
    await preparation?.cancel?.(orderId)
    throw error
  }
}

/** A mobile billing callback belongs to the captured order, never the current checkout intent. */
export async function loadPreparedBillingReturn(
  params: { purchase_order_id?: string; issue_id?: string; pg_provider?: string },
  bearerToken: string,
): Promise<KrPaymentOrder | null> {
  if (!params.purchase_order_id) {
    return null
  }
  const order = await getKrPaymentOrder(params.purchase_order_id, bearerToken)
  if (order.status === 'FAILED' || isKrOrderRetired(order)) {
    throw new Error('KR_PAYMENT_ORDER_NOT_EXECUTABLE')
  }
  if (
    (params.issue_id && params.issue_id !== order.issueId) ||
    (params.pg_provider === 'KAKAO_PAY' && order.paymentMethod !== 'KAKAO') ||
    (params.pg_provider === 'NAVER_PAY' && order.paymentMethod !== 'NAVER')
  ) {
    throw new Error('KR_PAYMENT_ORDER_MISMATCH')
  }
  return order
}

export function preparedChannelKey(order: KrPaymentOrder | null | undefined, legacyChannelKey: string): string {
  if (order?.authorityMode === 'MANUAL_CONFIRM_V2') {
    if (!order.channelKey) {
      throw new Error('KR_MANUAL_PAYMENT_CHANNEL_REQUIRED')
    }
    return order.channelKey
  }
  return legacyChannelKey
}

export async function confirmPreparedPayment(
  order: KrPaymentOrder | null | undefined,
  response: { paymentId: string; paymentToken?: string; txId?: string },
  bearerToken: string,
) {
  if (order?.authorityMode !== 'MANUAL_CONFIRM_V2') {
    return
  }
  if (response.paymentId !== order.orderId || !response.paymentToken || !response.txId) {
    throw new Error('KR_MANUAL_PAYMENT_APPROVAL_REQUIRED')
  }
  const provider = { TOSS: 'TOSS_PAYMENTS', KAKAO: 'KAKAO_PAY', NAVER: 'NAVER_PAY' }[
    order.paymentMethod as 'TOSS' | 'KAKAO' | 'NAVER'
  ]
  const params = {
    purchase_order_id: order.orderId,
    product_id: order.productId,
    subscribe_id: order.subscribeId,
    paymentId: response.paymentId,
    paymentToken: response.paymentToken,
    txId: response.txId,
    payment_type: 'LUMP_SUM',
    pg_provider: provider,
    amount: String(order.paymentAmount),
    complete: 'true',
  }
  const target = parseKrPaymentReturnTarget(krOrderResumePath(order).split('?')[0], params)
  if (!target || !provider) {
    throw new Error('KR_PAYMENT_RETURN_INVALID')
  }
  await withKrPaymentAuthReturn(target, async () => {
    try {
      await confirmPreparedPaymentReturn(params, bearerToken)
    } catch (error) {
      const recoveredWrite = z
        .object({
          cause: z.object({
            rawData: z.object({ error: z.object({ code: z.literal('AUTH_SESSION_RETRY_REQUIRED') }) }),
          }),
        })
        .safeParse(error)
      if (recoveredWrite.success) {
        // The generic client never replays POST. Preserve this exact approval for state-aware order recovery.
        await navigateToPaymentAwareAuthRecovery()
      }
      throw error
    }
  })
}

export async function confirmPreparedPaymentReturn(
  params: {
    purchase_order_id?: string
    paymentId?: string
    payment_id?: string
    merchant_uid?: string
    paymentToken?: string
    payment_token?: string
    txId?: string
    tx_id?: string
  },
  bearerToken: string,
) {
  if (!params.purchase_order_id) {
    return
  }
  const returnedPaymentId = params.paymentId ?? params.payment_id ?? params.merchant_uid
  if (returnedPaymentId && returnedPaymentId !== params.purchase_order_id) {
    throw new Error('KR_PAYMENT_ORDER_MISMATCH')
  }
  const order = await getKrPaymentOrder(params.purchase_order_id, bearerToken)
  if (order.authorityMode !== 'MANUAL_CONFIRM_V2') {
    return
  }
  const paymentToken = params.paymentToken ?? params.payment_token
  const txId = params.txId ?? params.tx_id
  if (order.status === 'FAILED' || isKrOrderRetired(order)) {
    throw new Error('KR_PAYMENT_ORDER_FAILED')
  }
  if (order.status === 'PREPARED') {
    if (!paymentToken || !txId) {
      throw new Error('KR_MANUAL_PAYMENT_APPROVAL_REQUIRED')
    }
    await confirmKrPaymentOrder(order.orderId, bearerToken, { paymentToken, txId })
  } else if (order.status === 'PROCESSING') {
    // The committed journal owns an in-flight approval. Callback data cannot replace it.
    await confirmKrPaymentOrder(order.orderId, bearerToken)
  }
  // PAID/FULFILLMENT_PENDING only resume result/entitlement polling, without another PG confirmation.
  const url = new URL(window.location.href)
  url.searchParams.delete('paymentToken')
  url.searchParams.delete('payment_token')
  window.history.replaceState(window.history.state, '', url)
}
